import {
  ErrPayloadSchema,
  MatchEndPayloadSchema,
  MatchEventPayloadSchema,
  MatchPausePayloadSchema,
  MatchStartPayloadSchema,
  channelName,
  type MatchEndPayload,
  type MatchEventPayload,
  type MatchPausePayload,
  type MatchStartPayload,
  type SubWindow,
} from "@legacy-manager/shared";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { fatorDaPartida, lanceDaPartida, useT } from "../i18n";
import type { ClubeDaVisao, JogadorDoMeuClube } from "../online/esquemas";
import { useRelogio, useSessao } from "../online/sessao";
import { Bar, Crest } from "../ui/atoms";
import { TrocaNoBanco, type JogadorDaTroca } from "../ui/TrocaNoBanco";
import { tocarGol } from "../ui/somDeGol";
import { estiloDoGol } from "./LiveMatch";

/**
 * Partida ao vivo transmitida pelo servidor.
 *
 * Parece a `LiveMatch` do modo solo, e por dentro é o contrário dela. Lá o
 * motor roda no aparelho e a tela pede o próximo pedaço quando quer; aqui o
 * servidor já sabe o resultado inteiro e **segura a entrega**, mandando os
 * lances em ritmo controlado pelo canal `match:{id}` (docs/WEBSOCKET.md 4).
 *
 * Isso muda duas coisas na interface, e as duas são de propósito:
 *
 * Não existe botão de ritmo nem de pular. O jogo é o mesmo para todo mundo que
 * está assistindo, e adiantar para um adiantaria para ninguém.
 *
 * A substituição não é uma troca local, é uma decisão enviada. Ela vale dentro
 * da janela de pausa, o servidor confirma ou recusa, a simulação só segue
 * depois, e a troca entra no resultado oficial da rodada como qualquer outra
 * decisão. Por isso a tela mostra o que foi mandado, e não o que ela gostaria
 * que tivesse acontecido.
 *
 * O `matchId` chega de fora: quem descobre qual partida está no ar é o hub da
 * liga, pelo `match.start` que o servidor publica no canal `league:{id}` quando
 * a transmissão abre.
 */

interface Lance extends MatchEventPayload {
  /** Ordem de chegada, porque dois lances podem cair no mesmo minuto. */
  ordem: number;
}

export function PartidaAoVivo({
  matchId,
  clubes,
  meuClubeId,
  elenco,
  aoFechar,
}: {
  matchId: string;
  /** Clubes da liga, da visão filtrada. É o que dá escudo e cor aos dois lados. */
  clubes: ClubeDaVisao[];
  meuClubeId: string | null;
  /** Meu elenco, para a tela saber o nome de quem entra e de quem sai. */
  elenco: JogadorDoMeuClube[];
  aoFechar: () => void;
}): ReactNode {
  const t = useT();
  const sessao = useSessao();

  const [inicio, definirInicio] = useState<MatchStartPayload | null>(null);
  const [lances, definirLances] = useState<Lance[]>([]);
  const [pausa, definirPausa] = useState<(MatchPausePayload & { em: number }) | null>(null);
  const [fim, definirFim] = useState<MatchEndPayload | null>(null);
  const [trocas, definirTrocas] = useState<Array<{ sai: string; entra: string }>>([]);
  const [recusa, definirRecusa] = useState<string | null>(null);
  const fimDaLista = useRef<HTMLDivElement | null>(null);

  const canal = useMemo(() => channelName("match", matchId), [matchId]);
  const agora = useRelogio(pausa !== null);

  useEffect(() => {
    sessao.socket.assinar(canal);
    let contador = 0;

    const parar = sessao.socket.ouvir((tipo, carga, deQualCanal) => {
      // `err` chega sem canal, porque é resposta a uma mensagem e não evento de
      // um canal. É assim que a recusa de uma substituição aparece.
      if (tipo === "err" && deQualCanal === null) {
        const lido = ErrPayloadSchema.safeParse(carga);
        if (lido.success) definirRecusa(lido.data.message);
        return;
      }
      if (deQualCanal !== canal) return;

      switch (tipo) {
        case "match.start": {
          const lido = MatchStartPayloadSchema.safeParse(carga);
          if (lido.success) definirInicio(lido.data);
          return;
        }
        case "match.event": {
          const lido = MatchEventPayloadSchema.safeParse(carga);
          if (!lido.success) return;
          contador += 1;
          const ordem = contador;
          definirLances((atuais) => [...atuais, { ...lido.data, ordem }]);
          return;
        }
        case "match.pause": {
          const lido = MatchPausePayloadSchema.safeParse(carga);
          if (lido.success) definirPausa({ ...lido.data, em: Date.now() });
          return;
        }
        case "match.end": {
          const lido = MatchEndPayloadSchema.safeParse(carga);
          if (lido.success) definirFim(lido.data);
          definirPausa(null);
          return;
        }
        default:
          return;
      }
    });

    return () => {
      parar();
      sessao.socket.desassinar(canal);
    };
  }, [sessao, canal]);

  // A janela fecha sozinha quando o prazo vence: o servidor segue o jogo com ou
  // sem decisão, e deixar a lista aberta só enganaria quem está escolhendo.
  useEffect(() => {
    if (!pausa) return;
    const id = window.setTimeout(() => {
      definirPausa(null);
    }, pausa.deadlineMs);
    return () => window.clearTimeout(id);
  }, [pausa]);

  useEffect(() => {
    fimDaLista.current?.scrollIntoView({ block: "end" });
  }, [lances.length]);

  // Gol novo toca o som; os lances que chegaram de uma vez ao abrir, não.
  const lancesVistos = useRef<number | null>(null);
  useEffect(() => {
    if (lancesVistos.current === null) {
      lancesVistos.current = lances.length;
      return;
    }
    const novos = lances.slice(lancesVistos.current);
    lancesVistos.current = lances.length;
    const gol = novos.filter((lance) => lance.kind === "goal").at(-1);
    if (gol && meuClubeId) tocarGol(gol.clubId === meuClubeId);
  }, [lances, meuClubeId]);

  const casa = clubes.find((clube) => clube.name === inicio?.home) ?? null;
  const fora = clubes.find((clube) => clube.name === inicio?.away) ?? null;

  const golsDe = (clubeId: string | null): number =>
    clubeId === null
      ? 0
      : lances.filter((lance) => lance.kind === "goal" && lance.clubId === clubeId).length;

  const golsCasa = fim ? fim.score[0] : golsDe(casa?.id ?? null);
  const golsFora = fim ? fim.score[1] : golsDe(fora?.id ?? null);

  const meuLado =
    meuClubeId && casa?.id === meuClubeId
      ? "home"
      : meuClubeId && fora?.id === meuClubeId
        ? "away"
        : null;
  const minhaEscalacao = meuLado && inicio ? inicio.lineups[meuLado] : null;

  const porId = new Map(elenco.map((jogador) => [jogador.id, jogador]));
  const emCampo = minhaEscalacao
    ? [
        ...minhaEscalacao.starters.filter((id) => !trocas.some((troca) => troca.sai === id)),
        ...trocas.map((troca) => troca.entra),
      ]
    : [];
  const banco = minhaEscalacao
    ? minhaEscalacao.bench.filter((id) => !trocas.some((troca) => troca.entra === id))
    : [];

  const mandarTroca = (sai: string, entra: string, janela: SubWindow): void => {
    definirRecusa(null);
    sessao.socket.enviar("match.sub", { matchId, out: sai, in: entra, window: janela });
    definirTrocas((atuais) => [...atuais, { sai, entra }]);
  };

  const paraATroca = (ids: string[]): JogadorDaTroca[] =>
    ids.map((id) => {
      const jogador = porId.get(id);
      return { id, name: jogador?.name ?? id, pos: jogador?.pos ?? "", strength: jogador?.strength ?? 0 };
    });

  const ultimoMinuto = lances[lances.length - 1]?.minute ?? 0;
  const restanteDaPausa = pausa ? Math.max(0, pausa.em + pausa.deadlineMs - agora) : 0;

  return (
    <div
      role="dialog"
      aria-label={t("partidaOnline.aria")}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--gutter)",
        zIndex: 28,
      }}
    >
      <div
        className="card"
        style={{ width: "min(720px, 100%)", maxHeight: "92vh", overflow: "auto", padding: 18 }}
      >
        {!sessao.aoVivo && (
          <p style={{ margin: "0 0 10px", textAlign: "center", fontSize: 12, color: "var(--warning)" }}>
            {t("partidaOnline.desconectado")}
          </p>
        )}

        <div
          className="row"
          style={{ gap: 14, justifyContent: "center", alignItems: "center", margin: "4px 0" }}
        >
          <div className="row" style={{ gap: 8, flex: 1, justifyContent: "flex-end", minWidth: 0 }}>
            <span style={{ fontWeight: 700, textAlign: "right" }}>
              {inicio?.home ?? t("comum.vazio")}
            </span>
            {casa && <Crest colors={casa.colors} short={casa.short} size={34} />}
          </div>
          <span className="num" style={{ fontSize: 30, fontWeight: 800 }}>
            {golsCasa} x {golsFora}
          </span>
          <div className="row" style={{ gap: 8, flex: 1, minWidth: 0 }}>
            {fora && <Crest colors={fora.colors} short={fora.short} size={34} />}
            <span style={{ fontWeight: 700 }}>{inicio?.away ?? t("comum.vazio")}</span>
          </div>
        </div>

        <div style={{ textAlign: "center", marginBottom: 12 }}>
          <span className="num" style={{ fontSize: 15, fontWeight: 700, color: "var(--primary)" }}>
            {fim
              ? t("partidaOnline.fimDeJogo")
              : t("partidaOnline.minuto", { minuto: ultimoMinuto })}
          </span>
        </div>

        <div
          className="scroll"
          style={{
            maxHeight: pausa ? "24vh" : "42vh",
            background: "var(--surface-2)",
            borderRadius: 8,
            padding: "8px 10px",
          }}
        >
          {!inicio && (
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              {t("partidaOnline.conectando")}
            </p>
          )}
          {inicio && lances.length === 0 && (
            <p className="muted" style={{ margin: 0, fontSize: 12 }}>
              {t("partidaOnline.esperando")}
            </p>
          )}
          {lances.map((lance) => (
            <div
              key={lance.ordem}
              className="row"
              style={{
                gap: 10,
                padding: "3px 0",
                alignItems: "baseline",
                ...estiloDoGol(lance.kind === "goal" && meuClubeId !== null, lance.clubId === meuClubeId),
              }}
            >
              <span
                className="num"
                style={{ width: 30, color: "var(--text-dim)", fontSize: 11.5, flexShrink: 0 }}
              >
                {t("partidaOnline.minuto", { minuto: lance.minute })}
              </span>
              <span
                style={{
                  fontSize: 12.5,
                  fontWeight: lance.kind === "goal" ? 700 : 400,
                  color:
                    lance.kind === "goal"
                      ? meuClubeId === null
                        ? "var(--primary)"
                        : lance.clubId === meuClubeId
                          ? "var(--success)"
                          : "var(--danger)"
                      : lance.kind === "card"
                        ? "var(--warning)"
                        : lance.kind === "injury"
                          ? "var(--danger)"
                          : "inherit",
                }}
              >
                {lanceDaPartida(lance.key, lance.params)}
              </span>
            </div>
          ))}
          <div ref={fimDaLista} />
        </div>

        {recusa && (
          <p role="alert" style={{ margin: "10px 0 0", color: "var(--danger)", fontSize: 12.5 }}>
            {recusa}
          </p>
        )}

        {pausa && !fim && (
          <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
            <div className="row" style={{ justifyContent: "space-between", gap: 10 }}>
              <span className="label">{t("partidaOnline.pausa")}</span>
              <span className="label-muted">
                {t("partidaOnline.janela", { janela: pausa.window })}
                {" · "}
                {t("partidaOnline.tempoParaDecidir", {
                  segundos: Math.ceil(restanteDaPausa / 1000),
                })}
              </span>
            </div>

            {meuLado === null ? (
              <p className="muted" style={{ margin: "8px 0 0", fontSize: 11.5 }}>
                {t("partidaOnline.semClubeNaPartida")}
              </p>
            ) : (
              <>
                <div className="scroll" style={{ maxHeight: "30vh", marginTop: 10 }}>
                  <TrocaNoBanco
                    emCampo={paraATroca(emCampo)}
                    banco={paraATroca(banco)}
                    rotuloDoCampo={t("partidaOnline.emCampo")}
                    rotuloDoBanco={t("partidaOnline.banco")}
                    vazioDoCampo={t("partidaOnline.ninguemEmCampo")}
                    vazioDoBanco={t("partidaOnline.semReservas")}
                    bloqueado={false}
                    aoTrocar={(sai, entra) => mandarTroca(sai, entra, pausa.window)}
                  />
                </div>
                <div className="row" style={{ gap: 8, marginTop: 10 }}>
                  <span className="muted" style={{ fontSize: 11, flex: 1 }}>
                    {trocas.length > 0 ? t("partidaOnline.trocaEnviada") : t("partidaOnline.seguirSemMexer")}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {fim && <Encerramento fim={fim} />}

        <button className="ghost" style={{ width: "100%", marginTop: 14 }} onClick={aoFechar}>
          {t("partidaOnline.fechar")}
        </button>
      </div>
    </div>
  );
}

function Encerramento({ fim }: { fim: MatchEndPayload }): ReactNode {
  const t = useT();
  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--border)", paddingTop: 12 }}>
      <div
        className="grid"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(110px, 1fr))", gap: 10 }}
      >
        <Numero rotulo={t("partidaOnline.posse")} casa={fim.stats.home.possession} fora={fim.stats.away.possession} />
        <Numero rotulo={t("partidaOnline.chances")} casa={fim.stats.home.chances} fora={fim.stats.away.chances} />
        <Numero rotulo={t("partidaOnline.finalizacoes")} casa={fim.stats.home.shots} fora={fim.stats.away.shots} />
        <Numero rotulo={t("partidaOnline.cartoes")} casa={fim.stats.home.cards} fora={fim.stats.away.cards} />
      </div>

      {fim.factors.length > 0 && (
        <>
          <div className="label" style={{ marginTop: 12 }}>
            {t("partidaOnline.porQueAconteceu")}
          </div>
          <div className="grid" style={{ gap: 6, marginTop: 6 }}>
            {fim.factors.map((fator) => (
              <div key={fator.key} className="row" style={{ gap: 8 }}>
                <Bar value={Math.round(fator.impact * 100)} />
                <span style={{ fontSize: 12 }}>{fatorDaPartida(fator.key, fator.params)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Numero({
  rotulo,
  casa,
  fora,
}: {
  rotulo: string;
  casa: number;
  fora: number;
}): ReactNode {
  return (
    <div style={{ textAlign: "center" }}>
      <div className="label-muted">{rotulo}</div>
      <div className="num" style={{ fontSize: 14, fontWeight: 700, marginTop: 2 }}>
        {Math.round(casa)} x {Math.round(fora)}
      </div>
    </div>
  );
}
